// AddressConfigCrud.jsx
import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../pages.css';
import {
    MButton,
    LoadingContext,
    M_StateActions,
    MHeader,
    NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';


//=====================================================================================================================================================================================
// Builds a lowerCamelCase key from a given string (used for DTO payload keys).
//=====================================================================================================================================================================================
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

//  Same rule used by DynamicForm to decide the form key for FKs: if Header exists use it, else Name + 'Id'
const getFkFormKey = (opt) => {
    const nameKey = opt?.Name || '';
    if (opt?.Header) return opt.Header;
    return nameKey?.endsWith('Id') ? nameKey : `${nameKey}Id`;
};


//=====================================================================================================================================================================================
// Function resume
// Checks if an Option is a boolean type.
//=====================================================================================================================================================================================
const isBoolean = (opt) => opt?.Type === 'boolean' || opt?.Type === 'bool';


// =============================================================================
// Component
// =============================================================================
export default function AddressConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const M_State = useSelector((state) => state.StateKeyValue);
    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    //=====================================================================================================================================================================================
    // Function resume
    // Read one-shot globals from the table and the selected tab id.
    //=====================================================================================================================================================================================
    const selectedTab = M_State.find((o) => o.key === 'selectedTab')?.value;
    const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
    const globalLine = M_State.find((obj) => obj.key === 'AddressLine')?.value;

    // Per-tab state
    const [id, setId] = useState(selectedTab);
    const [operation, setOperation] = useState(globalOperation ?? 'Error');
    const [sessionLine, setSessionLine] = useState(globalLine ?? {});
    const [addressInfo, setAddressInfo] = useState({});
    const [isFirst, setIsFirst] = useState(true);
    const [addressId, setAddressId] = useState(sessionLine?.Id);

    // Schema Options (raw; filtering happens inside DynamicForm)
    const [allOptions, setAllOptions] = useState([]);

    //  Refs to hold latest values across async callbacks to avoid stale closures
    const idRef = useRef(id);
    const addressIdRef = useRef(addressId);
    const operationRef = useRef(operation);
    useEffect(() => { idRef.current = id; }, [id]);
    useEffect(() => { addressIdRef.current = addressId; }, [addressId]);
    useEffect(() => { operationRef.current = operation; }, [operation]);

    //  Per-tab token to drop async setState after a tab switch
    const tabTokenRef = useRef(0);
    useEffect(() => { tabTokenRef.current += 1; }, [id]);

    // ref to inner DynamicForm to allow programmatic submit/getValues
    const formRef = useRef(null);

    //=====================================================================================================================================================================================
    // Function resume
    // Services (explicit baseUrl; BufferService avoids hooks in constructor).
    //=====================================================================================================================================================================================
    const svcAddresses = useMemo(() => new BufferService('Addresses', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcCreate = useMemo(() => new BufferService('Addresses/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
    const svcUpdate = useMemo(() => new BufferService('Addresses/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

    const isViewMode = (operation || '').toLowerCase() === 'view';

    //=====================================================================================================================================================================================
    // Function resume
    // Update the tab title on operation/name changes.
    //=====================================================================================================================================================================================
    useEffect(() => {
        if (!selectedTab || !operation) return;
        const i18nKey =
            operation === 'Create' ? 'LabelNewAddress' : operation === 'Edit' ? 'LabelEditAddress' : 'LabelViewAddress';
        const name = sessionLine?.Name ?? addressInfo?.Name ?? '';
        const base = t(i18nKey);
        const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
        renameTab(String(selectedTab), label);
    }, [selectedTab, operation, sessionLine?.Name, addressInfo?.Name]);

    //=====================================================================================================================================================================================
    // Function resume
    // Boot: restore per-tab snapshot, then fetch Options (and Details if needed).
    //=====================================================================================================================================================================================
    useEffect(() => {
        const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
        const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

        // Restore snapshot
        const snapInfo = pick('addressInfo');
        const snapIF = pick('isFirst');
        const snapOP = pick('operation');
        const snapSL = pick('sessionLine');
        const snapOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility with old key

        if (snapInfo) setAddressInfo(snapInfo);
        if (snapIF !== undefined) setIsFirst(snapIF);
        else if (isFirst) setIsFirst(false);

        const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
        const seedLine = globalLine !== undefined ? globalLine : snapSL;

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) {
            setSessionLine(seedLine);
            if (seedLine.Id) setAddressId(seedLine.Id);
        }
        if (Array.isArray(snapOptions)) setAllOptions(snapOptions);

        // Consume one-shot globals to keep tabs independent
        if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
        if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'AddressLine' }));

        // Fetch Options then details (if applicable)
        fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
            if (seedOperation === 'Create') {
                ctx.setIsLoad(false);
            } else if (seedOperation && seedLine?.Id) {
                await fetchDetails(seedLine?.Id);
                ctx.setIsLoad(false);
            } else {
                ctx.setIsLoad(false);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //  fkInitialQueries coming from saved tab snapshot for DynamicForm remote FK searches
    const fkInitialQueries = useMemo(() => {
        const tab = (M_State.find(o => o.key === id)?.value || []);
        return tab.find(x => x.key === 'dfFkQueries')?.value || {};
    }, [M_State, id]);

    //=====================================================================================================================================================================================
    // Function resume
    // Persist per-tab snapshot for isolation.
    //=====================================================================================================================================================================================
    const saveSnapshotTimer = useRef(null); //  small debounce to reduce Redux churn
    useEffect(() => {
        if (!id) return;

        // Merge RHF current values with entity so we persist unsaved edits across tabs
        const currentFormValues = formRef.current?.getValues?.() || {};
        const sanitize = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== ''));

        const infoToPersist =
            (operation || '').toLowerCase() === 'view'
                ? addressInfo
                : { ...addressInfo, ...sanitize(currentFormValues) };

        const sessionData = [
            { key: 'addressInfo', value: infoToPersist },
            { key: 'isFirst', value: isFirst },
            { key: 'operation', value: operation },
            { key: 'sessionLine', value: sessionLine },
            { key: 'allOptions', value: allOptions },
            //  also store the current entity id to avoid leaking across tabs
            { key: 'addressId', value: addressId },
        ];

        if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        saveSnapshotTimer.current = setTimeout(() => {
            dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
        }, 80);

        return () => {
            if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
        };
    }, [id, addressInfo, isFirst, operation, sessionLine, allOptions, addressId, dispatch]);

    //=====================================================================================================================================================================================
    // Function resume
    // Track tab switch and restore the snapshot or refetch.
    //=====================================================================================================================================================================================
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
        if (newId && newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    ///======================================================================================================================================================
    // getSessionStorageData
    // Restores a tab snapshot if available; otherwise fetches options and details as needed.
    // Steps:
    // 1) Try to load a persisted snapshot for this tab id.
    // 2) If found, restore options and entity state; else refetch options and details.
    ///======================================================================================================================================================
    const getSessionStorageData = async (sessionID) => {
        ctx.setIsLoad(true);
        const data = M_State.find((obj) => obj.key === sessionID)?.value;
        if (data) {
            const pick = (k) => data.find((x) => x.key === k)?.value;

            setAddressInfo(pick('addressInfo') || {});
            setIsFirst(pick('isFirst') ?? false);
            setOperation(pick('operation') ?? 'Error');
            setSessionLine(pick('sessionLine') || {});
            const restoredId = (pick('sessionLine')?.Id) ?? (pick('addressInfo')?.Id) ?? (pick('addressId') ?? null);
            setAddressId(restoredId ?? null);

            const tempOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
            if (Array.isArray(tempOptions)) setAllOptions(tempOptions);

            ctx.setIsLoad(false);
            return;
        }
        // No snapshot : refetch
        await fetchOptions(operation, sessionLine, { skipDetails: true }).finally(async () => {
            if (operation !== 'Create' && sessionLine?.Id) await fetchDetails(sessionLine?.Id);
            ctx.setIsLoad(false);
        });
    };

    //=====================================================================================================================================================================================
    // React to language changes: re-fetch Options so labels reflect the current language, and the tab title is updated.
    //=====================================================================================================================================================================================
    useEffect(() => {
        if (!operation || isFirst) return; // skip first render or no operation set
        fetchOptions(operation, sessionLine, { skipDetails: true });

        const i18nKey =
            operation === 'Create' ? 'LabelNewAddress' : operation === 'Edit' ? 'LabelEditAddress' : 'LabelViewAddress';
        const name = sessionLine?.Name ?? addressInfo?.Name ?? '';
        const base = t(i18nKey);
        const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
        renameTab(String(selectedTab), label);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    ///======================================================================================================================================================
    // fetchOptions
    // Fetches raw Options for Address. Optionally fetch entity details for Edit/View.
    // Steps:
    // 1) Call /Options for Addresses and store raw options.
    // 2) Optionally fetch entity details for Edit/View.
    // Note: FK datasets are not preloaded here; DynamicForm will handle lookups.
    ///======================================================================================================================================================
    const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
        const myToken = tabTokenRef.current;
        ctx.setIsLoad(true);
        try {
            const res = await svcAddresses.options(); // /api/Options for Addresses
            if (isFailurePayload(res) || res?.name === 'AxiosError') {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                ctx.setIsLoad(false);
                return;
            }
            //  Ignore state updates if the user switched tabs while awaiting the request
            if (myToken !== tabTokenRef.current) return;
            setAllOptions(res || []);

            const mode = op || operationRef.current || 'View';
            if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
                await fetchDetails(line?.Id || sessionLine?.Id);
            }
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
        } finally {
            //  Only toggle loader if we are still on the same tab token
            if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
        }
    };

    //=====================================================================================================================================================================================
    // Fetch entity details (Edit/View). Keeps raw record; DynamicForm handles hydration.
    //=====================================================================================================================================================================================
    const fetchDetails = async (idToGet) => {
        const myToken = tabTokenRef.current;
        ctx.setIsLoad(true);
        try {
            const res = await svcAddresses.getId(idToGet);
            if (isFailurePayload(res)) {
                toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
                return null;
            }
            if (myToken !== tabTokenRef.current) return null;
            setAddressInfo(res || {});
            setAddressId(res?.Id);
            return res || null;
        } catch (err) {
            toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
            return null;
        } finally {
            if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
        }
    };

    ///======================================================================================================================================================
    // buildPayloadFromValues
    // Builds DTO payload from values emitted by DynamicForm and raw options.
    // Steps:
    // 1) Iterate over options to determine the correct key and source value.
    // 2) For FK use Header/NameId. Treat 0 or '' as null.
    // 3) For booleans coerce to boolean.
    // 4) Lower camel case keys for DTO and assemble object.
    ///======================================================================================================================================================
    const buildPayloadFromValues = (values) => {
        const options = Array.isArray(allOptions) ? allOptions : [];
        const entries = [];

        for (const opt of options) {
            if (!opt) continue;

            const keyInValues = opt.IsForeignKey ? getFkFormKey(opt) : opt.Name;
            const payloadKey = toCamel(opt.IsForeignKey ? getFkFormKey(opt) : (opt.Header || opt.Name));

            let v;
            if (opt.IsForeignKey) {
                const rawId = values?.[keyInValues];
                v = (rawId == null || rawId === '' || Number(rawId) === 0) ? null : Number(rawId);
            } else if (isBoolean(opt)) {
                v = !!values?.[keyInValues];
            } else {
                v = values?.[keyInValues];
            }

            if (v !== undefined) entries.push([payloadKey, v]);
        }

        return Object.fromEntries(entries);
    };

    ///======================================================================================================================================================
    // onSubmitDynamic
    // Receives values from DynamicForm and performs create/update flow.
    // Steps:
    // 1) Build DTO payload.
    // 2) Call create or update service.
    // 3) Toast result and close tab.
    ///======================================================================================================================================================
    const onSubmitDynamic = useCallback(
        async (values) => {
            ctx.setIsLoad(true);
            try {
                const payload = buildPayloadFromValues(values);
                const currentId = addressIdRef.current; //  read latest id from ref
                const op = (operationRef.current || '').toLowerCase(); //  read latest operation from ref

                const res =
                    op === 'create'
                        ? await svcCreate.post(payload)
                        : await svcUpdate.update(currentId, payload);

                if (isFailurePayload(res)) {
                    toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
                    return;
                }
                toastMessages('success', '', op === 'create' ? t('AddressCreated') : t('AddressUpdated'));
                handleTabClose(idRef.current);
            } catch (err) {
                toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
            } finally {
                ctx.setIsLoad(false);
            }
        },
        // do not depend directly on id/operation/addressId to avoid stale closures on quick tab switches
        [buildPayloadFromValues, ctx, handleTabClose, svcCreate, svcUpdate, t, toastMessages]
    );

    ///======================================================================================================================================================
    // triggerFormSubmit
    // Triggers DynamicForm submit programmatically via imperative ref.
    ///======================================================================================================================================================
    const triggerFormSubmit = () => {
        formRef.current?.submit?.();
    };

    ///======================================================================================================================================================
    // onRefresh
    // Re-fetches options, and details if applicable for current mode.
    ///======================================================================================================================================================
    const onRefresh = useCallback(async () => {
        const op = operationRef.current;
        const line = sessionLine;
        await fetchOptions(op, line);
        if (op !== 'Create' && line?.Id) {
            await fetchDetails(line.Id);
        }
    }, [fetchOptions, fetchDetails, sessionLine]);

    ///======================================================================================================================================================
    // handleFormValuesChange
    // Receives small patches from DynamicForm and persists them in the per-tab snapshot.
    // Also keeps dfFkQueries map for remote FK inputs.
    ///======================================================================================================================================================
    const handleFormValuesChange = useCallback(
        (patch, meta) => {
            if (!patch || !idRef.current) return;

            const prevTabState = M_State.find((o) => o.key === idRef.current)?.value || [];
            const pick = (k) => prevTabState.find((x) => x.key === k)?.value;

            const mergedInfo = { ...(pick('addressInfo') || addressInfo || {}), ...patch };

            const updated = [
                ...prevTabState.filter((x) => x.key !== 'addressInfo' && x.key !== 'dfFkQueries'),
                { key: 'addressInfo', value: mergedInfo },
                ...(meta?.cause === 'fk-query'
                    ? [{ key: 'dfFkQueries', value: { ...(pick('dfFkQueries') || {}), [meta.name]: meta.query || '' } }]
                    : pick('dfFkQueries') != null
                        ? [{ key: 'dfFkQueries', value: pick('dfFkQueries') }]
                        : []),
            ];

            dispatch(M_StateActions.addM_State({ key: idRef.current, value: updated }));
        },
        [M_State, addressInfo, dispatch]
    );

    //=====================================================================================================================================================================================
    // JSX render: header, section title and DynamicForm card.
    //=====================================================================================================================================================================================
    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t('RefreshTable')}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={onRefresh}
                            label={t('RefreshTable')}
                        />
                        {!isViewMode && (
                            <MButton
                                tooltip={t('Save')}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={triggerFormSubmit}
                                label={t('Save')}
                            />
                        )}
                    </>
                }
            />

            <div style={{
                marginBottom: '30px',
                padding: '20px',
                background: 'var(--color-white)',
            }}>
                <div className="firstDivContainer">
                    <DynamicForm
                        key={`df-address-${id}-${operation}-${addressId ?? 'new'}`}
                        formRef={formRef}
                        mode={(operation || 'View').toLowerCase()}
                        schema={allOptions}
                        data={addressInfo}
                        onSubmit={onSubmitDynamic}
                        onValuesChange={handleFormValuesChange}
                        fkInitialQueries={fkInitialQueries}
                        title={t('InfoAddresses')}
                        containerClassName="alignInputsUserForm"
                    />
                </div>
            </div>
        </>
    );
}

AddressConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func
};
